/* oxlint-disable tsdoc/syntax */
import { CompletionContext } from '@codemirror/autocomplete';
import { EditorState } from '@codemirror/state';

import { verifyQuery } from '../dql/dql-linter.js';

// Look back a bounded amount while typing to keep per-keystroke checks predictable.
const SUBQUERY_PREFIX_LOOKBACK_CHARS = 100;

/**
 * Checks whether the cursor is at a position where a command or parameter is expected,
 * following a comma or pipe separator, with only whitespace in between.
 * @param context - the current completion context
 * @returns true if the cursor is at the start of a command or parameter, false otherwise
 */
function isStartOfCommandOrParameter(context: CompletionContext): boolean {
  return context.matchBefore(/[,|]\s+$/) !== null;
}

/**
 * Checks whether the user is typing a command prefix (e.g. `d`, `da`, `fet`)
 * at a valid command boundary: start of query, after a `|` or `,` separator,
 * or after a block comment close (`*\/`). The latter covers reopening
 * suggestions after ESC and continued typing following an inline block
 * comment, e.g. `/* note *\/ fetc`.
 * @param context - the current completion context
 * @returns true if the user is typing a command prefix at a valid command boundary, false otherwise
 */
function isTypingCommandPrefixAtCommandBoundary(
  context: CompletionContext,
): boolean {
  return (
    context.matchBefore(/(?:^|[|,]\s*|\*\/\s*)[A-Za-z][A-Za-z0-9]*$/) !== null
  );
}

/**
 * Covers nested subqueries where a command starts after `[` on a new indented line,
 * e.g. `lookup(..., [\n    fetc ...)`.
 *
 * Uses a manual slice instead of context.matchBefore because `[` may be on a
 * previous line, which matchBefore cannot see.
 *
 * @param context - the current completion context
 * @returns true if the user is typing a command prefix at the start of a subquery, false otherwise
 */
function isTypingCommandPrefixAtSubqueryStart(
  context: CompletionContext,
): boolean {
  // Slice at most SUBQUERY_PREFIX_LOOKBACK_CHARS before the cursor — bounding input size
  // prevents the regex from backtracking excessively on large documents.
  const slice = context.state.doc.sliceString(
    Math.max(0, context.pos - SUBQUERY_PREFIX_LOOKBACK_CHARS),
    context.pos,
  );

  // Safe and fast regex: we only check a small slice and match a command
  // prefix at the end of the slice.
  return /\[\s*[A-Za-z]+$/.test(slice);
}

/**
 * Checks whether the user is typing a parameter prefix after a command.
 * This covers reopening suggestions after ESC when typing continues within
 * the first parameter token at query start, for example
 * `fetch app` -> `fetch appl`.
 * @param context - the current completion context
 * @returns true if the user is typing a parameter prefix after a command, false otherwise
 */
function isTypingParameterPrefixAfterCommand(
  context: CompletionContext,
): boolean {
  const commandAndParameter = context.matchBefore(
    /^[A-Za-z][A-Za-z0-9]*\s+[A-Za-z_][A-Za-z0-9_.]*\s*$/,
  );

  return commandAndParameter !== null;
}

/**
 * Checks whether the cursor is within a digit-led token, such as an integer, a decimal number,
 * or a number followed by a unit suffix (e.g. `1`, `1mon`, `1.5`, `1.5h`).
 * @param context - the current completion context
 * @returns true if the cursor is within a digit-led token, false otherwise
 */
function isTypingNumericContext(context: CompletionContext): boolean {
  return context.matchBefore(/\d[\w.]*$/) !== null;
}

/**
 * Allows opening autocomplete when the cursor is at a token boundary:
 * immediately before whitespace, before a comment, or at end of content.
 * This supports selected-token replacement where content can still follow
 * after whitespace, while blocking autocomplete in the middle of identifiers.
 * @param state - the current editor state
 * @returns true if the cursor is at a token boundary, false otherwise
 */
function isAtTokenBoundary(state: EditorState): boolean {
  const afterCursor = state.doc.slice(state.selection.main.to).toString();
  const immediateCharacterAfterCursor = afterCursor.slice(0, 1);
  const afterCursorTrimmed = afterCursor.trimStart();

  return (
    immediateCharacterAfterCursor.trim().length === 0 ||
    afterCursorTrimmed.startsWith('//') ||
    afterCursorTrimmed.startsWith('/*')
  );
}

/**
 * Checks whether the query is invalid at the end.
 *
 * @param query - The query string to check
 * @returns A promise that resolves to true if the query is invalid at the end, false otherwise
 */
export async function isInvalidAtEnd(query: string): Promise<boolean> {
  const trimmedQuery = query.trimEnd();
  const { valid = true, notifications = [] } =
    (await verifyQuery(trimmedQuery)) ?? {};

  const hasErrorNotificationAtEnd = notifications?.some(
    (notification) =>
      notification.severity === 'ERROR' &&
      notification.syntaxPosition?.end.index === trimmedQuery.length - 1,
  );

  return !valid && hasErrorNotificationAtEnd;
}

/**
 * Determines if the autocomplete dropdown should be opened.
 *
 * @param update - Information on the current changes from the update listener
 * @param state - EditorState
 * @returns true when the autocomplete should be opened
 */
export async function openAutocomplete(
  update: {
    isTyping: boolean;
    isDeleting: boolean;
    isFocused: boolean;
  },
  state: EditorState,
): Promise<boolean> {
  const { isTyping, isDeleting, isFocused } = update;

  const context = new CompletionContext(state, state.selection.main.to, false);

  // While deleting, suppress reopen if the cursor still has a run of trailing
  // whitespace (including empty lines) behind it. This avoids popup flicker
  // while users clean up spacing. Reopen can happen again once the run shrinks
  // to at most one trailing whitespace character.
  if (isDeleting) {
    const beforeCursor = state.doc.sliceString(
      Math.max(0, state.selection.main.to - 2),
      state.selection.main.to,
    );

    // We only slice the 2 chars directly before the cursor.
    // If both are whitespace (`\s\s$`), it means there are at least two
    // trailing whitespace chars before the cursor, so we suppress reopen.
    if (/\s\s$/.test(beforeCursor)) {
      return false;
    }
  }

  if (
    isTyping &&
    (isTypingCommandPrefixAtCommandBoundary(context) ||
      isTypingCommandPrefixAtSubqueryStart(context) ||
      isTypingParameterPrefixAfterCommand(context) ||
      isTypingNumericContext(context)) &&
    isAtTokenBoundary(state)
  ) {
    return true;
  }

  // open autocomplete when an empty editor receives focus
  if (isFocused && state.doc.toString().trim() === '') {
    return true;
  }

  const hasWhitespaceBefore = context.matchBefore(/\s/);

  // open autocomplete when the user is typing following a command (==keyword)
  if (
    (isTyping || isDeleting) &&
    context.tokenBefore(['keyword']) &&
    hasWhitespaceBefore
  ) {
    return true;
  }

  // open autocomplete if the user is typing following a comma or pipe
  if (isTyping && isStartOfCommandOrParameter(context)) {
    return true;
  }

  // open autocomplete if query is invalid and a space was typed beforehand
  if (isTyping && hasWhitespaceBefore) {
    const query = state.doc.slice(0, state.selection.main.to).toString();
    if (await isInvalidAtEnd(query)) {
      return true;
    }
  }

  const characterAfter = state.doc
    .slice(state.selection.main.to, state.selection.main.to + 1)
    .toString();
  const hasSpaceOrEndOfContentAfter = characterAfter.trim().length === 0;

  // open autocomplete if
  //  - a whitespace was deleted
  //  - the last char was deleted
  if (isDeleting && hasSpaceOrEndOfContentAfter) {
    return true;
  }

  return false;
}
